<template>
  <div class="contact-management">
    <div class="header">
      <h3>联系人管理</h3>
      <el-button type="primary" size="small" :disabled="editDisabled" @click="showCreateDialog()">
        <el-icon><Plus /></el-icon> 新增联系人
      </el-button>
    </div>

    <el-table :data="contacts" v-loading="loading" size="small">
      <el-table-column prop="contact_name" label="联系人" width="120" />
      <el-table-column prop="department" label="部门/职位" width="150" />
      <el-table-column prop="phone" label="电话" width="130" />
      <el-table-column prop="email" label="邮箱" width="150" />
      <el-table-column prop="is_default" label="默认联系人" width="100">
        <template #default="{ row }">
          <el-tag v-if="row.is_default" type="success" size="small">是</el-tag>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="150" fixed="right">
        <template #default="{ row }">
          <el-button size="small" :disabled="editDisabled" @click="handleEdit(row)">编辑</el-button>
          <el-button size="small" type="danger" :disabled="editDisabled" @click="handleDelete(row)">删除</el-button>
        </template>
      </el-table-column>
    </el-table>

    <!-- 新增/编辑联系人对话框 -->
    <el-dialog
      :title="isEditing ? '编辑联系人' : '新增联系人'"
      v-model="showFormDialog"
      width="500px"
    >
      <el-form
        :model="formData"
        :rules="formRules"
        ref="formRef"
        label-width="100px"
      >
        <el-form-item label="联系人" prop="contact_name">
          <el-input v-model="formData.contact_name" placeholder="请输入联系人姓名" />
        </el-form-item>
        <el-form-item label="部门/职位">
          <el-input v-model="formData.department" placeholder="请输入部门或职位" />
        </el-form-item>
        <el-form-item label="电话">
          <el-input v-model="formData.phone" placeholder="请输入联系电话" />
        </el-form-item>
        <el-form-item label="邮箱">
          <el-input v-model="formData.email" placeholder="请输入邮箱地址" />
        </el-form-item>
        <el-form-item label="默认联系人">
          <el-switch v-model="formData.is_default" />
        </el-form-item>
      </el-form>

      <template #footer>
        <span class="dialog-footer">
          <el-button @click="showFormDialog = false">取消</el-button>
          <el-button type="primary" @click="submitForm">确认</el-button>
        </span>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { Plus } from '@element-plus/icons-vue'
import { 
  getCustomerContacts, 
  createCustomerContact, 
  updateCustomerContact, 
  deleteCustomerContact 
} from '@/api/customers'

const props = defineProps({
  customerId: {
    type: Number,
    required: true
  },
  editDisabled: {
    type: Boolean,
    default: false
  }
})

const loading = ref(false)
const contacts = ref([])
const showFormDialog = ref(false)
const isEditing = ref(false)
const formRef = ref()

const formData = reactive({
  contact_name: '',
  department: '',
  phone: '',
  email: '',
  is_default: false
})

const formRules = {
  contact_name: [
    { required: true, message: '请输入联系人姓名', trigger: 'blur' }
  ]
}

// 监听customerId变化
watch(() => props.customerId, (newVal) => {
  if (newVal) {
    loadContacts()
  }
})

// 加载联系人数据
const loadContacts = async () => {
  if (!props.customerId) return
  
  loading.value = true
  try {
    const response = await getCustomerContacts(props.customerId)
    contacts.value = response.data
  } catch (error) {
    ElMessage.error('加载联系人失败')
  } finally {
    loading.value = false
  }
}

// 打开新增对话框
const showCreateDialog = () => {
  isEditing.value = false
  resetForm()
  showFormDialog.value = true
}

// 打开编辑对话框
const handleEdit = (row) => {
  isEditing.value = true
  Object.assign(formData, row)
  showFormDialog.value = true
}

// 删除联系人
const handleDelete = async (row) => {
  try {
    await ElMessageBox.confirm(
      `确定要删除联系人 "${row.contact_name}" 吗？`,
      '警告',
      { type: 'warning' }
    )
    
    await deleteCustomerContact(props.customerId, row.contact_id)
    ElMessage.success('联系人删除成功')
    loadContacts()
  } catch (error) {
    if (error !== 'cancel') {
      ElMessage.error('删除失败')
    }
  }
}

// 提交表单
const submitForm = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
    
    if (isEditing.value) {
      await updateCustomerContact(props.customerId, formData.contact_id, formData)
      ElMessage.success('联系人更新成功')
    } else {
      await createCustomerContact(props.customerId, formData)
      ElMessage.success('联系人创建成功')
    }
    
    showFormDialog.value = false
    loadContacts()
  } catch (error) {
    if (error.response?.data?.message) {
      ElMessage.error(error.response?.message)
    }
  }
}

// 重置表单
const resetForm = () => {
  if (formRef.value) {
    formRef.value.resetFields()
  }
  Object.assign(formData, {
    contact_name: '',
    department: '',
    phone: '',
    email: '',
    is_default: false
  })
}

// 初始化加载
onMounted(() => {
  if (props.customerId) {
    loadContacts()
  }
})

// 暴露方法给父组件
defineExpose({
  showCreateDialog
})
</script>

<style scoped>
.contact-management {
  padding: 10px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.header h3 {
  margin: 0;
  font-size: 16px;
  color: #303133;
}
</style>